import type { ProductCombination, ProductCombinationOptionGroup } from '~~/shared/types/store'

/**
 * Wybór wariantu do wyceny w sklepie katalogowym — w szybkim podglądzie
 * i na karcie produktu.
 *
 * Tam klient decyduje, o co pyta — więc KAŻDA grupa atrybutów (rozmiar,
 * kolor…) ma jawny wybór, a do zapytania idzie dokładnie jedna istniejąca
 * kombinacja. Bez tego zapytanie niosło sam produkt albo kolor domyślny i nie
 * było wiadomo, jaki wariant wycenić.
 *
 * Kod kombinacji z backendu to id atrybutów sklejone `_` (`3_217` = rozmiar L
 * + kolor Blekitny). Porównujemy ZBIORY id — kolejność nie jest kontraktem.
 */

const normalize = (value: unknown) => String(value ?? '').trim().toLowerCase()

/** Grupa wybierana przyciskami rozmiaru podglądu (te same, co w sklepach sprzedażowych). */
export function isSizeGroup(group: ProductCombinationOptionGroup): boolean {
  return normalize(group.name) === 'rozmiar'
}

/**
 * Atrybut kombinacji domyślnej grupy (`groups[id].default` z backendu), jeśli
 * mapper go podaje. Czytany defensywnie: pole dokłada karta produktu i do jej
 * wdrożenia go nie ma — wtedy wybór startuje od pierwszej dostępnej wartości.
 */
function defaultIdOf(group: ProductCombinationOptionGroup): number | undefined {
  const value = (group as { defaultId?: unknown }).defaultId

  return typeof value === 'number' && value > 0 ? value : undefined
}

function attributeIds(combination: ProductCombination): Set<number> {
  return new Set(
    combination.combinationCode
      .split('_')
      .map(part => Number(part.trim()))
      .filter(id => Number.isInteger(id) && id > 0),
  )
}

/** Wybrane id atrybutów per nazwa grupy. */
export type VariantPicks = Record<string, number>

export interface QuoteVariant {
  combination: ProductCombination
  /** Atrybuty wyboru w kolejności grup — ten sam kształt co SelectedCombination karty produktu. */
  attributes: Array<{ group: string, value: string }>
  /** „Rozmiar: L, Kolor: Blekitny" — w kolejności grup z backendu. */
  label: string
  /** Kombinacja domyślna produktu — od niej pochodzą nazwa i SKU produktu. */
  isDefault: boolean
}

function findCombination(
  combinations: readonly ProductCombination[],
  picked: number[],
): ProductCombination | undefined {
  return combinations.find((combination) => {
    const ids = attributeIds(combination)

    return ids.size === picked.length && picked.every(id => ids.has(id))
  })
}

export interface VariantSelection {
  /** Wybór każdej grupy — zawsze tworzy istniejącą kombinację; bez niej grupa zostaje bez wyboru. */
  picks: VariantPicks
  /** Grupy z przyciskami (`shown`), okrojone do wartości dostępnych przy wyborze wyżej. */
  groups: ProductCombinationOptionGroup[]
}

/**
 * Wybór wariantu i przyciski, które warto pokazać — liczone razem, z góry na dół.
 *
 * Rozmiar z własnych przycisków (grupa spoza `shown`) jest ustalony. Każda
 * kolejna grupa (w kolejności `options`) dostaje tylko wartości, które z wyborem
 * grup WYŻEJ tworzą istniejącą kombinację — koszulka ma 32 kolory, a w danym
 * rozmiarze część z nich nie istnieje i nie ma po co rozpychać widoku. Z tych
 * wartości wybieramy kliknięcie/adres (`preferred`), potem kombinację domyślną,
 * potem pierwszą. Ograniczenie tylko „od góry": gdyby grupy chowały sobie
 * wartości nawzajem, część kombinacji byłaby nieosiągalna, a kliknięcie w grupie
 * niżej nigdy nie unieważnia wyboru wyżej.
 *
 * Rozmiar bez przycisków (pusta lista rozmiarów — wybierany jak kolor, więc
 * w `shown`) startuje z kombinacji domyślnej jak każda grupa — inaczej wybór
 * byłby niepełny i zapytanie szło bez kombinacji.
 */
export function resolveVariantSelection(
  options: readonly ProductCombinationOptionGroup[],
  combinations: readonly ProductCombination[],
  { sizeLabel, shown, preferred }: {
    sizeLabel: string
    shown: readonly ProductCombinationOptionGroup[]
    preferred: VariantPicks
  },
): VariantSelection {
  const picks: VariantPicks = {}
  const groups: ProductCombinationOptionGroup[] = []
  const shownNames = new Set(shown.map(group => group.name))

  for (const group of options) {
    if (isSizeGroup(group) && !shownNames.has(group.name)) {
      const item = group.items.find(i => normalize(i.value) === normalize(sizeLabel))
      if (item) picks[group.name] = item.id
    }
  }

  for (const group of options) {
    if (picks[group.name] !== undefined) continue
    const available = group.items.filter(item =>
      isPickAvailable(options, combinations, { ...picks, [group.name]: item.id }))
    // Pusta lista = wybrany rozmiar nie ma żadnej pełnej kombinacji. Wtedy bez
    // wyboru: zgadnięta wartość trafiłaby do etykiety i linku listy zapytania.
    const chosen = [preferred[group.name], defaultIdOf(group)]
      .map(id => available.find(item => item.id === id))
      .find(item => item !== undefined)
      ?? available[0]
    if (chosen) picks[group.name] = chosen.id
    if (shownNames.has(group.name) && available.length > 0) groups.push({ ...group, items: available })
  }

  return { picks, groups }
}

/**
 * Czy da się dobrać resztę grup tak, żeby powstała istniejąca kombinacja?
 * Służy do odsiewania wartości, których w tym zestawie nie ma (BOSTON: XL
 * tylko w białym).
 */
export function isPickAvailable(
  options: readonly ProductCombinationOptionGroup[],
  combinations: readonly ProductCombination[],
  picks: VariantPicks,
): boolean {
  const fixed = options
    .map(group => picks[group.name])
    .filter((id): id is number => id !== undefined)

  return combinations.some((combination) => {
    const ids = attributeIds(combination)

    return ids.size === options.length && fixed.every(id => ids.has(id))
  })
}

/** Kombinacja dla pełnego wyboru — `undefined`, gdy wybór niepełny albo takiej nie ma. */
export function resolveQuoteVariant(
  options: readonly ProductCombinationOptionGroup[],
  combinations: readonly ProductCombination[],
  picks: VariantPicks,
): QuoteVariant | undefined {
  if (options.length === 0 || combinations.length === 0) return undefined

  const attributes: QuoteVariant['attributes'] = []
  const picked: number[] = []
  for (const group of options) {
    const item = group.items.find(i => i.id === picks[group.name])
    if (!item) return undefined
    picked.push(item.id)
    attributes.push({ group: group.name, value: item.value })
  }

  const combination = findCombination(combinations, picked)
  if (!combination) return undefined

  return {
    combination,
    attributes,
    label: attributes.map(a => `${a.group}: ${a.value}`).join(', '),
    isDefault: options.every((group) => {
      const defaultId = defaultIdOf(group)

      return defaultId !== undefined && defaultId === picks[group.name]
    }),
  }
}
